import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Copy, ExternalLink, PenSquare, RefreshCw, X } from 'lucide-react';
import type { PlatformPost } from '../../../shared/types';
import { getPlatformSpec } from '../../../shared/platforms';
import { api, ApiError } from '../../lib/api';
import { qk } from '../../lib/query';
import { formatWhen } from '../../lib/format';
import { useWorkspaceStore, useWorkspaceTimezone } from '../../stores/workspace';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  PlatformIcon,
  Skeleton,
  SkeletonText,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '../ui';
import { PlatformPostStatusBadge, PostStatusBadge } from './PostStatusBadge';

export interface PostDetailDrawerProps {
  /** null closes the drawer; a post id opens (and fetches) it. */
  postId: string | null;
  onClose: () => void;
}

/**
 * Slide-over detail panel for a post: raw content, one tab per platform
 * variant with its content/error/external link, retry and duplicate actions.
 * Shared by the Posts list and the Calendar (both owned by WEB-3).
 */
export function PostDetailDrawer({ postId, onClose }: PostDetailDrawerProps) {
  const open = Boolean(postId);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const timezone = useWorkspaceTimezone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const query = useQuery({
    queryKey: qk.post(workspaceId, postId ?? ''),
    queryFn: () => api.posts.get(postId as string),
    enabled: open,
  });

  const post = query.data?.post ?? null;

  // Reset (and re-pick) the active tab whenever a different post opens.
  useEffect(() => {
    setActiveTab(null);
  }, [postId]);
  useEffect(() => {
    if (!post?.platformPosts.length) return;
    if (!activeTab || !post.platformPosts.some((pp) => pp.id === activeTab)) {
      setActiveTab(post.platformPosts[0]?.id ?? null);
    }
  }, [post, activeTab]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  const invalidate = () => {
    if (!postId) return;
    void queryClient.invalidateQueries({ queryKey: qk.post(workspaceId, postId) });
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'posts'] });
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'calendar'] });
  };

  const retry = useMutation({
    mutationFn: (platformPostId: string) => api.platformPosts.publish(platformPostId),
    onSuccess: (data) => {
      invalidate();
      if (data.result.ok) {
        toast({
          title: `${getPlatformSpec(data.result.platform).shortName} published`,
          tone: 'success',
        });
      } else {
        toast({
          title: 'Publish failed again',
          description: data.result.error ?? undefined,
          tone: 'error',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Could not retry the publish',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const duplicate = useMutation({
    mutationFn: async () => {
      if (!post) throw new Error('No post loaded');
      const created = await api.posts.create({
        title: post.title ?? undefined,
        rawContent: post.rawContent,
        mediaIds: post.media.map((asset) => asset.id),
        platforms: post.platformPosts.map((pp) => pp.platform),
      });
      return created.post;
    },
    onSuccess: (duplicated) => {
      toast({ title: 'Duplicated to a new draft', tone: 'success' });
      onClose();
      navigate(`/composer/${duplicated.id}`);
    },
    onError: (error) => {
      toast({
        title: 'Could not duplicate this post',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  if (typeof document === 'undefined') return null;

  const editable = post ? post.status !== 'archived' : false;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-overlay backdrop-blur-[2px]"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={post?.title ?? 'Post detail'}
            initial={{ x: 32, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 32, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex h-full w-full max-w-xl flex-col border-l border-border bg-surface shadow-pop"
          >
            <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
              <div className="min-w-0 space-y-1.5">
                {query.isLoading ? (
                  <Skeleton className="h-5 w-48" />
                ) : (
                  <h2 className="truncate text-base font-semibold text-fg">
                    {post?.title?.trim() || 'Untitled post'}
                  </h2>
                )}
                {post && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-subtle">
                    <PostStatusBadge status={post.status} />
                    {post.createdByUser && (
                      <span className="inline-flex items-center gap-1.5">
                        <Avatar
                          name={post.createdByUser.name}
                          email={post.createdByUser.email}
                          src={post.createdByUser.avatarUrl}
                          size="xs"
                        />
                        {post.createdByUser.name ?? post.createdByUser.email}
                      </span>
                    )}
                    <span>Updated {formatWhen(post.updatedAt, timezone)}</span>
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon-sm" aria-label="Close" onClick={onClose}>
                <X className="size-4" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {query.isLoading ? (
                <div className="space-y-4">
                  <SkeletonText lines={3} />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : query.isError ? (
                <Alert tone="danger" title="Could not load this post">
                  {query.error instanceof ApiError ? query.error.message : 'Try again in a moment.'}
                </Alert>
              ) : post ? (
                <div className="space-y-5">
                  {post.rawContent && (
                    <div className="rounded-lg border border-dashed border-border bg-elevated/40 p-3">
                      <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-subtle">
                        Raw content
                      </p>
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-muted">
                        {post.rawContent}
                      </p>
                    </div>
                  )}

                  {post.platformPosts.length === 0 ? (
                    <Alert tone="info" title="No platform variants yet">
                      Open this post in the composer to generate drafts for each platform.
                    </Alert>
                  ) : (
                    <Tabs value={activeTab ?? post.platformPosts[0].id} onValueChange={setActiveTab}>
                      <TabsList>
                        {post.platformPosts.map((pp) => (
                          <TabsTrigger
                            key={pp.id}
                            value={pp.id}
                            badge={
                              pp.status === 'failed' ? (
                                <span className="size-1.5 rounded-full bg-danger" aria-hidden />
                              ) : undefined
                            }
                          >
                            <PlatformIcon platform={pp.platform} size="xs" variant="soft" />
                            {getPlatformSpec(pp.platform).shortName}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {post.platformPosts.map((pp) => (
                        <TabsContent key={pp.id} value={pp.id}>
                          <PlatformPostPanel
                            platformPost={pp}
                            timezone={timezone}
                            onRetry={() => retry.mutate(pp.id)}
                            retrying={retry.isPending && retry.variables === pp.id}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  )}
                </div>
              ) : null}
            </div>

            {post && (
              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-elevated/30 px-5 py-3">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={duplicate.isPending}
                  leftIcon={<Copy className="size-3.5" />}
                  onClick={() => duplicate.mutate()}
                >
                  Duplicate to composer
                </Button>
                {editable && (
                  <Button
                    size="sm"
                    leftIcon={<PenSquare className="size-3.5" />}
                    onClick={() => {
                      onClose();
                      navigate(`/composer/${post.id}`);
                    }}
                  >
                    Edit in composer
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function PlatformPostPanel({
  platformPost,
  timezone,
  onRetry,
  retrying,
}: {
  platformPost: PlatformPost;
  timezone: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  const spec = getPlatformSpec(platformPost.platform);
  const when = platformPost.publishedAt ?? platformPost.scheduledAt;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <PlatformPostStatusBadge status={platformPost.status} />
        {platformPost.simulated && (
          <Badge tone="info" size="sm">
            Simulated
          </Badge>
        )}
        {when && <span className="text-xs text-subtle">{formatWhen(when, timezone)}</span>}
        {platformPost.retryCount > 0 && (
          <span className="text-xs text-subtle">
            {platformPost.retryCount} {platformPost.retryCount === 1 ? 'retry' : 'retries'}
          </span>
        )}
      </div>

      {platformPost.meta.title && (
        <p className="text-sm font-medium text-fg">{platformPost.meta.title}</p>
      )}

      <div className="rounded-lg border border-border bg-elevated/30 p-3">
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-fg">
          {platformPost.content || <span className="text-subtle">No content yet.</span>}
        </p>
      </div>

      {platformPost.meta.hashtags && platformPost.meta.hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {platformPost.meta.hashtags.map((tag) => (
            <Badge key={tag} tone="accent" size="sm">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {platformPost.error && (
        <Alert
          tone="danger"
          title="Publish failed"
          icon={<AlertTriangle className="size-4" />}
          action={
            <Button
              size="sm"
              variant="danger"
              loading={retrying}
              leftIcon={<RefreshCw className="size-3.5" />}
              onClick={onRetry}
            >
              Retry
            </Button>
          }
        >
          {platformPost.error}
        </Alert>
      )}

      {platformPost.externalUrl && (
        <a
          href={platformPost.externalUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
        >
          <ExternalLink className="size-3.5" />
          View on {spec.shortName}
        </a>
      )}
    </div>
  );
}
